import * as React from 'react'
import { useLocation, type Location } from 'react-router'

const SAVED_SCROLL_POSITION: Record<string, number> = {}
let PREVIOUS_LOCATION: Location | undefined = undefined

type ScrollRestorationConfig = {
  delay?: number
  restoreScroll?: boolean
  resetScroll?: () => boolean
}

export function useScrollRestoration({
  delay = 0,
  restoreScroll = true,
  resetScroll = () => true,
}: ScrollRestorationConfig = {}) {
  const location = useLocation()

  // saving the scroll position for the location we just left
  React.useLayoutEffect(() => {
    if (PREVIOUS_LOCATION !== undefined) {
      SAVED_SCROLL_POSITION[(PREVIOUS_LOCATION ?? location).key] = window.scrollY
    }
    PREVIOUS_LOCATION = location
  }, [location])

  // restore the scroll position for the location we just entered
  React.useLayoutEffect(() => {
    const handle = setTimeout(() => {
      const scrollPosition = SAVED_SCROLL_POSITION[location.key]

      // if there is a saved scroll position, we restore it
      if (restoreScroll && scrollPosition !== undefined) {
        window.scrollTo(0, scrollPosition)
        return
      }

      // otherwise if there is a hash, scroll to the hash
      if (location.hash) {
        const el = document.getElementById(location.hash.slice(1))
        if (el) {
          el.scrollIntoView()
          return
        }
      }

      // otherwise scroll to top
      if (resetScroll()) {
        window.scrollTo(0, 0)
      }
    }, delay)

    return () => {
      window.clearTimeout(handle)
    }
  }, [location])
}
